import { endMinute, localDate, startMinute, type Opportunity } from '../../shared/opportunity';
import { estimateTravel, type TravelEstimate } from './travel';

/**
 * Conflict and feasibility analysis for a day's schedule.
 *
 * Two distinct failure modes, and the difference matters to a planner:
 *
 *   overlap  — the events run at the same time. Unfixable; one has to go.
 *   tight    — they don't overlap, but you cannot physically get from one to
 *              the other in the gap between them. Fixable by leaving early or
 *              arriving late, so it is a warning rather than an error.
 *
 * Both are computed from data we already have. Events with no published end
 * time use the assumed one-hour duration, which is why the UI marks those
 * blocks: a conflict derived from an assumption should be legible as one.
 */

export interface ScheduleItem {
  event: Opportunity;
  /** Present when this item and the previous one are back to back. */
  travelFromPrevious: TravelEstimate | null;
  /** Minutes of slack after travel; negative means you cannot make it. */
  slackMinutes: number | null;
}

export interface DayPlan {
  dayKey: string;
  allDay: Opportunity[];
  timed: ScheduleItem[];
  overlaps: [Opportunity, Opportunity][];
  tight: { from: Opportunity; to: Opportunity; shortBy: number; travel: TravelEstimate }[];
}

const endOf = (e: Opportunity) => endMinute(e) ?? (startMinute(e) ?? 0) + 60;

/** Where an event sits on the clock, for the purpose of the overlap scan. */
export type SpanOf = (event: Opportunity) => { start: number; end: number };

const publishedSpan: SpanOf = (e) => ({ start: startMinute(e) ?? 0, end: endOf(e) });

/**
 * `spanOf` decides when each event runs, for both scans. Callers on My
 * Schedule pass the *attended* span: an event you are leaving at noon does not
 * clash with one that starts at noon, however long the programme says it runs,
 * and the journey between them has the time you actually gave it.
 */
export function buildDayPlan(
  dayKey: string,
  events: Opportunity[],
  spanOf: SpanOf = publishedSpan,
): DayPlan {
  const allDay = events.filter((e) => e.isAllDay);
  const timed = events
    .filter((e) => !e.isAllDay && startMinute(e) != null)
    .sort((a, b) => spanOf(a).start - spanOf(b).start);

  const overlaps: [Opportunity, Opportunity][] = [];
  const spans = new Map(timed.map((e) => [e.id, spanOf(e)]));
  // Every pair rather than a scan with an early break: cheap on a day's worth
  // of events, and correct however the spans fall.
  for (let i = 0; i < timed.length; i += 1) {
    for (let j = i + 1; j < timed.length; j += 1) {
      const a = spans.get(timed[i].id);
      const b = spans.get(timed[j].id);
      if (!a || !b) continue;
      // Touching at the boundary is a handover, not a clash.
      if (a.start < b.end && b.start < a.end) overlaps.push([timed[i], timed[j]]);
    }
  }

  const tight: DayPlan['tight'] = [];
  const items: ScheduleItem[] = timed.map((event, i) => {
    if (i === 0) return { event, travelFromPrevious: null, slackMinutes: null };

    const prev = timed[i - 1];
    // Only meaningful for events that don't already overlap.
    if (spans.get(event.id)!.start < spans.get(prev.id)!.end) {
      return { event, travelFromPrevious: null, slackMinutes: null };
    }

    const travel = estimateTravel(prev, event);
    if (!travel) return { event, travelFromPrevious: null, slackMinutes: null };

    const gap = spans.get(event.id)!.start - spans.get(prev.id)!.end;
    const slack = gap - travel.minutes;
    if (slack < 0) tight.push({ from: prev, to: event, shortBy: -slack, travel });

    return { event, travelFromPrevious: travel, slackMinutes: slack };
  });

  return { dayKey, allDay, timed: items, overlaps, tight };
}

/**
 * Everything already on the schedule that runs at the same time as `target`.
 *
 * The companion to `findLegInto` in travelLegs: that one answers "can I get
 * there from my last event", this one answers "am I already busy". Sorted by
 * start time so the list reads in the order the day does.
 *
 * All-day events are excluded on both sides. A multi-day program with no
 * published hours technically covers every timed event on its day, and marking
 * all of them as conflicts would be noise rather than a warning.
 */
export function findOverlappingScheduled(
  target: Opportunity,
  scheduled: Opportunity[],
  spanOf: SpanOf = publishedSpan,
): Opportunity[] {
  if (target.isAllDay || startMinute(target) == null) return [];
  const { start, end } = spanOf(target);
  const day = localDate(target);

  return scheduled
    .filter((candidate) => {
      if (candidate.id === target.id) return false;
      if (localDate(candidate) !== day) return false;
      if (candidate.isAllDay || startMinute(candidate) == null) return false;
      // Touching at the boundary is a clean handover, not an overlap: an event
      // ending at 4:00 does not conflict with one starting at 4:00.
      const other = spanOf(candidate);
      return other.start < end && other.end > start;
    })
    .sort((a, b) => (startMinute(a) ?? 0) - (startMinute(b) ?? 0));
}
